import {Modal, ModalDialog, Button, Form, Col, Row, Accordion, Card} from 'react-bootstrap'
import Draggable from 'react-draggable'
import { t } from '../i18n'
import { BlockPicker } from "react-color";
import {useEffect, useRef, useState} from "react";
import './ConfigDialog.css'
import play, {MatchSound} from "../sounds";
import {toggleFullscreen} from "../utils";
import {
    AllianceOrientation,
    AllianceSelectionStyle, AwardsStyle,
    DisplayConfig,
    DisplayType, DualDivisionRankingStyle,
    RankingsFontSize,
    ScoringBarLocation
} from "./Config";
import {DisplayParamsType} from "./DisplayParams";
import {ModalDialogProps} from "react-bootstrap/ModalDialog";

// From https://stackoverflow.com/a/7557433
function isElementInViewport(el : HTMLElement) {
    const rect = el.getBoundingClientRect();

    return (rect.bottom >= 0 && rect.right >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight) && rect.left <= (window.innerWidth || document.documentElement.clientWidth));
}

function DraggableModalDialog(props : ModalDialogProps & {state: { x: number, y: number }, setState: (state: { x: number, y: number }) => void}) {
    const dialogEl = useRef<HTMLDivElement>(null);

    return <Draggable position={props.state} onStop={(_e, position) => {
        if(!dialogEl.current || !isElementInViewport(dialogEl.current)) {
            props.setState({x: 0, y: 0})
        } else {
            props.setState({x: position.x, y: position.y})
        }
    }}
                      cancel={"select,option,button,input,label,.colorPicker,.configAccordionButton"}
    ><div><ModalDialog {...props} ref={dialogEl}/></div></Draggable>
}

export default function ConfigDialog({show, handleClose, fieldCount, config, setConfig, isDualDivisionParent, style, showMeetRankings, remoteConfig, eventName, divisions} : {
    show: boolean,
    handleClose: () => void,
    fieldCount: number,
    config: DisplayConfig,
    setConfig: (cfg: DisplayConfig) => void,
    isDualDivisionParent?: boolean,
    style: DisplayParamsType["style"],
    showMeetRankings?: boolean,
    remoteConfig?: boolean,
    eventName?: string,
    divisions?: Record<number, string>
}) {
    const [showPicker, setShowPicker] = useState(false);
    const [oldConfig, setOldConfig] = useState<DisplayConfig>(config);
    const [position, setPosition] = useState({x: 0, y: 0})
    const [selectedSound, setSelectedSound] = useState<MatchSound>('matchStart');

    const isRemoteAwards = style === 'remoteAwards'
    const isCloudPit = style === 'cloudPit'
    const isTraditional = style === 'default'

    // save the old config so it can be restored on close
    useEffect(() => {
        setOldConfig(config)
    }, [show]);

    function updateConfig(newParams : Partial<DisplayConfig>) {
        setConfig(Object.assign({}, config, newParams))
    }

    const naturalDivisionOrder = Object.keys(divisions || {}).map(x => parseInt(x, 10)).sort((a, b) => a - b);
    const divisionOrder = config.divisionOrder && config.divisionOrder.length == naturalDivisionOrder.length ? config.divisionOrder : naturalDivisionOrder;

    const configurable = config.type === 'field' || config.type === 'audience' || config.type === 'cmpBar'
    return <Modal state={position} setState={setPosition} show={show} onHide={handleClose} size="lg" dialogAs={DraggableModalDialog} backdrop={false} enforceFocus={false} className="displayConfigModal">
        <Modal.Header closeButton>
            <Modal.Title className="draggableModalTitle">Display Options{eventName && eventName.length > 0 ? <span className={"h5"} style={{marginLeft: '.5em'}}>{`[${eventName}]`}</span> : null}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
            <Accordion id="configDialogBody" defaultActiveKey={"displaySettings"}>
                {isRemoteAwards || isCloudPit || remoteConfig ? null : <Card>
                    <Accordion.Toggle as={Card.Header} eventKey="audioTesting" className={"configAccordionButton"}>
                        {t`Audio Testing`}
                    </Accordion.Toggle>

                    <Accordion.Collapse eventKey="audioTesting">
                        <Card.Body>
                            <select value={selectedSound} onChange={(evt) => setSelectedSound(evt.currentTarget.value as MatchSound)}>
                                <option value="matchStart">MATCH start</option>
                                <option value="autoEnd">AUTO end</option>
                                <option value="controllers">Pick Up Controllers</option>
                                <option value="countdown">3-2-1</option>
                                <option value="startTeleop">TELEOP start</option>
                                <option value="endgameWarning">30-second Warning</option>
                                <option value="matchEnd">MATCH end</option>
                                <option value="abortMatch">MATCH abort</option>
                            </select>
                            <Button onClick={() => play(selectedSound)} variant={"secondary"} size={"sm"} className={"ml-2"}>Play</Button>
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>}

                <Card>
                    <Accordion.Toggle as={Card.Header} eventKey="displaySettings" className={"configAccordionButton"}>
                            {t`Display Settings`}
                    </Accordion.Toggle>

                    <Accordion.Collapse eventKey="displaySettings">
                        <Card.Body>
                            {isTraditional ? <Form.Group as={Row} controlId="displayName">
                                <Form.Label column sm="4">{t`Display Name`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control value={config.name} onChange={e => updateConfig({ name: e.target.value })} />
                                </Col>
                            </Form.Group> : null}
                            <Form.Group as={Row} controlId="displayType">
                                <Form.Label column sm="4">{t`Display Type`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.type} onChange={e => {
                                        const newValues : Partial<DisplayConfig> = { type: e.target.value as DisplayType, fieldStyleTimer: e.target.value === 'field' || e.target.value == "cmpBar" }
                                        if(newValues.fieldStyleTimer) {
                                            newValues.overlay = false;
                                        } else {
                                            newValues.overlay = true;
                                        }
                                        updateConfig(newValues)
                                    }}>
                                        {isCloudPit ? null : <option value='audience'>{t`Audience`}</option>}
                                        {isRemoteAwards || isCloudPit ? null : <option value='field'>{t`Field`}</option>}
                                        {isRemoteAwards ? null : <option value='pit'>{t`Pit`}</option>}
                                        {isCloudPit ? null :<option value='sponsor'>{t`Sponsor`}</option>}
                                        {isCloudPit || isRemoteAwards ? null :<option value='cmpBar'>{t`Championship Bar`}</option>}
                                    </Form.Control>
                                </Col>
                            </Form.Group>

                            {isRemoteAwards || isCloudPit ? null : <Form.Group as={Row} controlId="bindToField" style={{display: configurable ? undefined : 'none'}}>
                                <Form.Label column sm="4">{t`Bind to Field`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.bindToField} onChange={e => updateConfig({ bindToField: e.target.value })}>
                                        <option value='all'>{t`(All)`}</option>
                                        {[...Array(fieldCount).keys()].map(i => {
                                            const fieldNo = i + 1;
                                            return <option key={fieldNo} value={fieldNo}>{t`Field ${fieldNo}`}</option>
                                        })}
                                    </Form.Control>
                                </Col>
                            </Form.Group>}

                            {isRemoteAwards || isCloudPit ? <>
                                <Form.Label style={{display: configurable ? undefined : 'none'}}>{t`Overlay Configuration`}</Form.Label>
                                <Form.Group className="ml-4" controlId="audienceStyleTimer" style={{display: configurable ? undefined : 'none'}}>
                                    <Form.Check type="radio" name="timeStyle" label={t`Classic`} checked={!config.overlay} onChange={e => { if(e.target.checked) { updateConfig({ overlay: false }) } }} />
                                </Form.Group>
                                <Form.Group className="ml-4" controlId="overlay" style={{display: configurable ? undefined : 'none'}}>
                                    <Form.Check type="radio" name="timeStyle" label={t`Use Overlay`} checked={config.overlay} onChange={e => { if(e.target.checked) { updateConfig({ overlay: true }) } }} />
                                </Form.Group>
                            </> : <>
                                <Form.Label style={{display: configurable ? undefined : 'none'}}>{t`Timer/Overlay Configuration`}</Form.Label>
                                {/* Disabled for DECODE. Not removed because it may return in a future season */}
                                {/*<Form.Group className="ml-4" controlId="audienceStyleTimer" style={{display: configurable ? undefined : 'none'}}>*/}
                                {/*    <Form.Check type="radio" name="timeStyle" label={t`Use audience-style timer (with a little timer in the scoring bar)`} disabled={!config.liveScores} checked={!config.fieldStyleTimer && !config.overlay} onChange={e => { if(e.target.checked) { updateConfig({ overlay: false, fieldStyleTimer: false }) } }} />*/}
                                {/*</Form.Group>*/}

                                <Form.Group className="ml-4" controlId="fieldStyleTimer" style={{display: configurable ? undefined : 'none'}}>
                                    <Form.Check type="radio" name="timeStyle" label={t`Use field-style timer (with a big timer in the middle of the screen)`} checked={config.fieldStyleTimer} onChange={e => { if(e.target.checked) { updateConfig({ overlay: false, fieldStyleTimer: true }) } }} />
                                </Form.Group>
                                <Form.Group className="ml-4" controlId="overlay" style={{display: configurable ? undefined : 'none'}}>
                                    <Form.Check type="radio" name="timeStyle" label={t`Use Overlay`} checked={config.overlay} onChange={e => { if(e.target.checked) { updateConfig({ overlay: true, fieldStyleTimer: false }) } }} />
                                </Form.Group>
                            </>}
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>

                <Card style={{display: config.overlay ? undefined : 'none'}}>
                    <Accordion.Toggle as={Card.Header} eventKey="overlaySettings" className={"configAccordionButton"}>
                        {t`Overlay Settings`}
                    </Accordion.Toggle>

                    <Accordion.Collapse eventKey="overlaySettings">
                        <Card.Body className={"overlaySettings"}>
                            <div style={{display: 'flex'}}>
                                <button className="btn btn-secondary btn-sm" onClick={() => setShowPicker(true)}>Pick Color</button>
                                <div className="ml-2" onClick={() => setShowPicker(true)} style={{display: 'inline-block', width: '2em', height: '2em', background: config.overlayColor }}/>
                            </div>
                            { showPicker ? <div style={{ position: 'absolute', zIndex: 2 }} className={"colorPicker"}>
                                <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, right: 0 }} onClick={() => setShowPicker(false)}/>

                                <BlockPicker colors={['#00FF00', '#FF00FF', 'transparent']} color={config.overlayColor} triangle="hide" onChangeComplete={(e) => updateConfig({ overlayColor: e.hex })}/>
                            </div> : null }

                            {isRemoteAwards ? null : <>
                            <Form.Group as={Row} controlId="allianceSelectionStyle">
                                <Form.Label column sm="4">{t`Alliance Selection Display`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.allianceSelectionStyle} onChange={e => updateConfig({ allianceSelectionStyle: e.target.value as AllianceSelectionStyle })}>
                                        <option value='classic'>{t`Classic`}</option>
                                        <option value='hybrid'>{t`Hybrid`}</option>
                                    </Form.Control>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} controlId="awardsStyle">
                                <Form.Label column sm="4">{t`Awards Display`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.awardsStyle} onChange={e => updateConfig({ awardsStyle: e.target.value as AwardsStyle })}>
                                        <option value='classic'>{t`Classic`}</option>
                                        <option value='overlay'>{t`Overlay`}</option>
                                    </Form.Control>
                                </Col>
                            </Form.Group></> }
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>
                {isRemoteAwards ? null : <Card>
                    <Accordion.Toggle as={Card.Header} eventKey="advancedSettings" className={"configAccordionButton"}>
                        {t`Advanced Settings`}
                    </Accordion.Toggle>

                    <Accordion.Collapse eventKey="advancedSettings">
                        <Card.Body>
                            {isRemoteAwards || isCloudPit ? null : <Form.Group as={Row} controlId="scoringBarLocation" style={{display: configurable ? undefined : 'none'}}>
                                <Form.Label column sm="4">{t`Scoring Bar Location`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.scoringBarLocation} onChange={e => updateConfig({ scoringBarLocation: e.target.value as ScoringBarLocation })}>
                                        <option value='bottom'>{t`Bottom`}</option>
                                        <option value='top'>{t`Top`}</option>
                                    </Form.Control>
                                </Col>
                            </Form.Group> }

                            {isRemoteAwards || isCloudPit ? null : <Form.Group as={Row} controlId="allianceOrientation" style={{display: configurable ? undefined : 'none'}}>
                                <Form.Label column sm="4">{t`Alliance Orientation`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.allianceOrientation} onChange={e => updateConfig({ allianceOrientation: e.target.value as AllianceOrientation })}>
                                        <option value='standard'>{t`Standard (Red on Left)`}</option>
                                        <option value='flipped'>{t`Flipped (Red on Right)`}</option>
                                    </Form.Control>
                                </Col>
                            </Form.Group>}

                            {isRemoteAwards ? null : <Form.Group as={Row} controlId="rankingsFontSize" style={{display: config.type !== 'sponsor' ? undefined : 'none'}}>
                                <Form.Label column sm="4">{t`Rankings Font Size`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.rankingsFontSize} onChange={e => updateConfig({ rankingsFontSize: e.target.value as RankingsFontSize })}>
                                        <option value='larger'>{t`Larger`}</option>
                                        <option value='smaller'>{t`Smaller (for screens closer to the viewers)`}</option>
                                    </Form.Control>
                                </Col>
                            </Form.Group>}
                            {isRemoteAwards || !showMeetRankings ? null : <Form.Group controlId="rankingsAllTeams" style={{display: config.type !== 'sponsor' ? undefined : 'none'}}>
                                <Form.Check label={t`Rankings Show All League Teams`} checked={config.rankingsAllTeams} onChange={e => updateConfig({ rankingsAllTeams: e.target.checked })}/>
                            </Form.Group>}


                            {isDualDivisionParent ? <Form.Group as={Row} controlId="dualDivisionRankingStyle" style={{display: config.type !== 'sponsor' ? undefined : 'none'}}>
                                <Form.Label column sm="4">{t`Dual Division Rankings Format`}</Form.Label>
                                <Col sm="8">
                                    <Form.Control as="select" custom value={config.dualDivisionRankingStyle} onChange={e => updateConfig({ dualDivisionRankingStyle: e.target.value as DualDivisionRankingStyle })}>
                                        <option value='sideBySide'>{t`Side By Side`}</option>
                                        <option value='stacked'>{t`Stacked`}</option>
                                    </Form.Control>
                                </Col>
                            </Form.Group> : null}

                            {isRemoteAwards || isCloudPit ? null : <>
                                <Form.Group controlId="mute" style={{display: configurable ? undefined : 'none'}}>
                                    <Form.Check label={t`Mute`} checked={config.mute} onChange={e => updateConfig({ mute: e.target.checked })} />
                                </Form.Group>
                                <Form.Group controlId="muteRandomizationResults" style={{display: configurable ? undefined : 'none'}}>
                                    <Form.Check label={t`Mute Results`} disabled={config.mute} checked={config.muteRandomizationResults} onChange={e => updateConfig({ muteRandomizationResults: e.target.checked })} />
                                </Form.Group>
                            </>}

                            {isDualDivisionParent && divisions ? <div>
                                <Form.Group controlId="inspectionGroupDivision">
                                    <Form.Check label={t`Group Inspection By Division`} checked={config.inspectionGroupDivision} onChange={e => updateConfig({ inspectionGroupDivision: e.target.checked })} />
                                </Form.Group>
                                <Row>
                                    <Col sm={"4"}>
                                        <Form.Label>{t`Division Ordering`}</Form.Label>
                                    </Col>
                                    <Col sm={"8"}>
                                        {divisionOrder.map((div, idx) => <Form.Control as="select" key={idx} custom value={div} onChange={e => {
                                            const newOrder = [...divisionOrder];
                                            newOrder[idx] = parseInt(e.target.value, 10);
                                            updateConfig({ divisionOrder: newOrder })
                                        }}>
                                            {naturalDivisionOrder.map((div) => <option value={div} key={div}>{divisions[div]}</option>)}
                                            <option value='-1'>{t`None`}</option>
                                        </Form.Control>)}
                                    </Col>
                                </Row>
                            </div> : null}
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>}
            </Accordion>
        </Modal.Body>

        <Modal.Footer>
            {!remoteConfig ? <Button variant="dark" onClick={() => toggleFullscreen()}>{t`Fullscreen`}</Button> : null}
            <Button variant="outline-secondary" onClick={() => setPosition({x: 0, y: 0})}>{t`Recenter`}</Button>
            <div style={{ flexGrow: 1 }} />
            <Button variant="secondary" onClick={() => {setConfig(oldConfig); handleClose()}}>{t`Cancel`}</Button>
            {/* Explicitly save so we don't show the dialog again on refresh if you accept the defaults */}
            <Button variant="primary" onClick={() => {setConfig(config); handleClose()}}>{t`Save`}</Button>
        </Modal.Footer>
    </Modal>
}